import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TodayIcon from '@mui/icons-material/Today';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TableViewIcon from '@mui/icons-material/TableView';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import api from '../../../utils/api';

const Report = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDevice, setSelectedDevice] = useState('');
  const [devices, setDevices] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [selectedTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const selectedDeviceObj = devices.find(d => d.deviceId === selectedDevice);
  const isTds = selectedDeviceObj?.ecUnit === 'TDS';
  const isIrrigation = selectedDeviceObj?.deviceType === 'irrigation';

  const getDeviceDisplayName = (device) => {
    if (!device) return selectedDevice || '';
    return `${device.deviceId} - ${device.location || 'Unknown Location'}`;
  };

  // Helper function to format date in local timezone (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (typeof date === 'string') return date;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Fetch devices on component mount
  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices');
      if (response.data.success) {
        const allDevices = response.data.data || [];
        setDevices(allDevices);
        if (allDevices.length > 0) {
          setSelectedDevice(allDevices[0].deviceId);
        } else {
          setSelectedDevice('');
        }
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to fetch devices');
    }
  };

  const fetchReportData = useCallback(async () => {
    if (!selectedDevice) return;

    setLoading(true);
    setError(null);

    try {
      const dateStr = formatDateForAPI(selectedDate);
      const response = await api.get(`/data/${selectedDevice}/report/daily`, {
        params: {
          date: dateStr,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          timezone: selectedTimezone
        }
      });

      if (response.data.success) {
        setReportData(response.data);
      } else {
        setError('Failed to fetch report data');
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      setError('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice, selectedDate, page, rowsPerPage, selectedTimezone]);

  // Fetch report data when device or date changes
  useEffect(() => {
    if (selectedDevice) {
      fetchReportData();
    }
  }, [selectedDevice, fetchReportData]);

  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
    setPage(0); // Reset to first page when device changes
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setPage(0); // Reset to first page when date changes
  };

  const handleTodayClick = () => {
    setSelectedDate(new Date());
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const parseApiTimestamp = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    if (typeof value === 'number') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      const hasZone =
        /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed) ||
        /[+-]\d{4}$/.test(trimmed);

      if (hasZone) {
        const d = new Date(trimmed);
        return isNaN(d.getTime()) ? null : d;
      }

      const isoNoZone = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
      if (isoNoZone.test(trimmed)) {
        const d = new Date(`${trimmed}Z`);
        return isNaN(d.getTime()) ? null : d;
      }

      const spaceSeparated = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?$/;
      const match = trimmed.match(spaceSeparated);
      if (match) {
        const d = new Date(`${match[1]}T${match[2]}${match[3] || ''}Z`);
        return isNaN(d.getTime()) ? null : d;
      }

      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : d;
    }

    return null;
  };

  const formatTime = (timestamp) => {
    const date = parseApiTimestamp(timestamp);
    if (!date) return 'N/A';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getPhColor = (value) => {
    if (value < 6.0) return '#f44336'; // Red for acidic
    if (value > 8.0) return '#ff9800'; // Orange for alkaline
    return '#4caf50'; // Green for optimal
  };

  const getEcColor = (value) => {
    if (value < 800) return '#2196f3'; // Blue for low
    if (value > 1800) return '#ff9800'; // Orange for high
    return '#4caf50'; // Green for optimal
  };

  const exportToExcel = async () => {
    if (!selectedDevice || !reportData) return;

    setExporting(true);
    try {
      const dateStr = formatDateForAPI(selectedDate);
      let allReadings = [];
      let summary = null;
      let offset = 0;
      const limit = 1000; // Maximum allowed by backend
      let hasMoreData = true;

      // Fetch all data in batches
      while (hasMoreData) {
        const response = await api.get(`/data/${selectedDevice}/report/daily`, {
          params: {
            date: dateStr,
            limit: limit,
            offset: offset,
            timezone: selectedTimezone
          }
        });

        if (response.data.success) {
          const batchData = response.data.data;
          
          // Store summary from first batch
          if (offset === 0) {
            summary = batchData.summary;
          }
          
          // Add readings to our collection
          allReadings = [...allReadings, ...batchData.readings];
          
          // Check if we have more data
          hasMoreData = batchData.readings.length === limit;
          offset += limit;
        } else {
          hasMoreData = false;
        }
      }

      if (summary && allReadings.length > 0) {
        // Create a new workbook
        const workbook = XLSX.utils.book_new();
        
        const summaryTitle = isIrrigation ? 'Daily Irrigation Outputs Report' : 'Daily Sensor Data Report';
        const summaryData = [
          [summaryTitle],
          [''],
          ['Report Information'],
          ['Date', selectedDate.toLocaleDateString('en-US')],
          ['Device', getDeviceDisplayName(devices.find(d => d.deviceId === selectedDevice))],
          ['Device ID', selectedDevice],
          ['Export Date', new Date().toLocaleString('en-US')],
          ['Timezone', selectedTimezone],
          [''],
          ['Summary Statistics'],
          ['Total Readings', summary.totalReadings],
          ...(isIrrigation ? [
            ['Enabled Count', summary.enabledCount],
            ['CH1 ON Count', summary.outputsOnCount?.ch1 ?? 0],
            ['CH2 ON Count', summary.outputsOnCount?.ch2 ?? 0],
            ['CH3 ON Count', summary.outputsOnCount?.ch3 ?? 0],
            ['CH4 ON Count', summary.outputsOnCount?.ch4 ?? 0],
            ['Data Period Start', summary.firstReading ? formatTime(summary.firstReading) : 'N/A'],
            ['Data Period End', summary.lastReading ? formatTime(summary.lastReading) : 'N/A']
          ] : [
            ['Average pH', summary.avgPh],
            ['pH Range', `${summary.minPh} - ${summary.maxPh}`],
            [isTds ? 'Average TDS (ppm)' : 'Average EC (μS/cm)', summary.avgEc],
            [isTds ? 'TDS Range (ppm)' : 'EC Range (μS/cm)', `${summary.minEc} - ${summary.maxEc}`],
            ['Data Period Start', summary.firstReading ? formatTime(summary.firstReading) : 'N/A'],
            ['Data Period End', summary.lastReading ? formatTime(summary.lastReading) : 'N/A']
          ])
        ];

        const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
        summarySheet['!cols'] = [
          { width: 25 },
          { width: 30 }
        ];

        if (isIrrigation) {
          const dataHeaders = ['Time', 'Enabled', 'CH1', 'CH2', 'CH3', 'CH4'];
          const dataRows = allReadings.map((reading) => [
            formatTime(reading.timestamp),
            reading.enabled ? 'Enabled' : 'Disabled',
            reading.outputs?.ch1 ? 'ON' : 'OFF',
            reading.outputs?.ch2 ? 'ON' : 'OFF',
            reading.outputs?.ch3 ? 'ON' : 'OFF',
            reading.outputs?.ch4 ? 'ON' : 'OFF'
          ]);

          const dataSheet = XLSX.utils.aoa_to_sheet([dataHeaders, ...dataRows]);
          dataSheet['!cols'] = [
            { width: 15 },
            { width: 12 },
            { width: 8 },
            { width: 8 },
            { width: 8 },
            { width: 8 }
          ];

          XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
          XLSX.utils.book_append_sheet(workbook, dataSheet, 'Outputs');

          const fileName = `irrigation-outputs-report-${selectedDevice}-${dateStr}.xlsx`;
          XLSX.writeFile(workbook, fileName);
        } else {
          const dataHeaders = ['Time', 'pH Value', isTds ? 'TDS Value (ppm)' : 'EC Value (μS/cm)', 'Status'];
          const dataRows = allReadings.map(reading => [
            formatTime(reading.timestamp),
            reading.phValue,
            reading.ecValue,
            getStatusText(reading.phValue, reading.ecValue)
          ]);

          const dataSheet = XLSX.utils.aoa_to_sheet([dataHeaders, ...dataRows]);
          dataSheet['!cols'] = [
            { width: 15 },
            { width: 12 },
            { width: 18 },
            { width: 12 }
          ];

          const range = XLSX.utils.decode_range(dataSheet['!ref']);
          for (let row = 1; row <= range.e.r; row++) {
            const phCell = `B${row + 1}`;
            if (dataSheet[phCell]) {
              const phValue = parseFloat(dataSheet[phCell].v);
              if (phValue < 6.0 || phValue > 8.0) {
                dataSheet[phCell].s = {
                  fill: { fgColor: { rgb: "FFCCCC" } },
                  font: { color: { rgb: "CC0000" } }
                };
              } else {
                dataSheet[phCell].s = {
                  fill: { fgColor: { rgb: "CCFFCC" } },
                  font: { color: { rgb: "006600" } }
                };
              }
            }

            const ecCell = `C${row + 1}`;
            if (dataSheet[ecCell]) {
              const ecValue = parseFloat(dataSheet[ecCell].v);
              if (ecValue < 800 || ecValue > 1800) {
                dataSheet[ecCell].s = {
                  fill: { fgColor: { rgb: "FFF2CC" } },
                  font: { color: { rgb: "CC6600" } }
                };
              } else {
                dataSheet[ecCell].s = {
                  fill: { fgColor: { rgb: "CCFFCC" } },
                  font: { color: { rgb: "006600" } }
                };
              }
            }
          }

          XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
          XLSX.utils.book_append_sheet(workbook, dataSheet, 'Sensor Data');

          const fileName = `sensor-report-${selectedDevice}-${dateStr}.xlsx`;
          XLSX.writeFile(workbook, fileName);
        }
        
        // Clear any previous errors
        setError(null);
      } else {
        setError('No data available for export');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getStatusText = (phValue, ecValue) => {
    const phStatus = phValue < 6.0 ? 'Acidic' : phValue > 8.0 ? 'Alkaline' : 'Optimal';
    const ecStatus = ecValue < 800 ? 'Low' : ecValue > 1800 ? 'High' : 'Optimal';
    
    if (phStatus === 'Optimal' && ecStatus === 'Optimal') return 'Good';
    if (phStatus !== 'Optimal' || ecStatus !== 'Optimal') return 'Attention';
    return 'Warning';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 600, 
          color: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <TableViewIcon sx={{ fontSize: '2rem', color: '#41ab5d' }} />
          Daily Data Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View detailed sensor readings and daily statistics
        </Typography>
      </Box>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Device</InputLabel>
              <Select
                value={selectedDevice}
                onChange={handleDeviceChange}
                label="Device"
                sx={{ height: 56 }}
              >
                {devices.map((device) => (
                  <MenuItem key={device.deviceId} value={device.deviceId}>
                    {getDeviceDisplayName(device)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select Date
              </Typography>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                maxDate={new Date()}
                dateFormat="yyyy-MM-dd"
                customInput={
                  <TextField
                    fullWidth
                    variant="outlined"
                    sx={{ 
                      '& .MuiInputBase-input': { cursor: 'pointer' },
                      '& .MuiInputBase-root': { height: 56 }
                    }}
                  />
                }
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={handleTodayClick}
              startIcon={<TodayIcon />}
              fullWidth
              sx={{ 
                height: 56,
                borderColor: '#41ab5d',
                color: '#41ab5d',
                '&:hover': {
                  borderColor: '#2e7d32',
                  backgroundColor: 'rgba(65, 171, 93, 0.04)'
                }
              }}
            >
              Today
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              onClick={exportToExcel}
              startIcon={<FileDownloadIcon />}
              disabled={!reportData || !reportData.data.readings.length || exporting}
              fullWidth
              sx={{ 
                height: 56,
                backgroundColor: '#41ab5d',
                '&:hover': {
                  backgroundColor: '#2e7d32'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0'
                }
              }}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {reportData && reportData.data.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              border: '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                  Total Readings
                </Typography>
                <Typography variant="h4" component="div" sx={{ 
                  color: '#41ab5d', 
                  fontWeight: 600,
                  mb: 1
                }}>
                  {reportData.data.summary.totalReadings}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {isIrrigation ? (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Enabled Count
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ 
                      color: '#10b981', 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      {reportData.data.summary.enabledCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Snapshots with irrigation enabled
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Outputs ON Count
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {['ch1','ch2','ch3','ch4'].map((ch) => (
                        <Typography key={ch} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#666666', fontWeight: 500 }}>{ch.toUpperCase()}</span>
                          <span style={{ color: '#1a1a1a', fontWeight: 600 }}>
                            {reportData.data.summary.outputsOnCount?.[ch] ?? 0}
                          </span>
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Average pH
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ 
                      color: getPhColor(reportData.data.summary.avgPh), 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      {reportData.data.summary.avgPh}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Range: {reportData.data.summary.minPh} - {reportData.data.summary.maxPh}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  borderRadius: 2, 
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  '&:hover': { boxShadow: 3 }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      {isTds ? 'Average TDS' : 'Average EC'}
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ 
                      color: getEcColor(reportData.data.summary.avgEc), 
                      fontWeight: 600,
                      mb: 1
                    }}>
                      {reportData.data.summary.avgEc}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Range: {reportData.data.summary.minEc} - {reportData.data.summary.maxEc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2, 
              height: '100%',
              border: '1px solid #e0e0e0',
              '&:hover': { boxShadow: 3 }
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography color="text.secondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                  Data Period
                </Typography>
                <Typography variant="body1" component="div" sx={{ 
                  fontWeight: 600,
                  lineHeight: 1.3
                }}>
                  {reportData.data.summary.firstReading && reportData.data.summary.lastReading ? (
                    <>
                      {formatTime(reportData.data.summary.firstReading)}
                      <br />
                      to {formatTime(reportData.data.summary.lastReading)}
                    </>
                  ) : (
                    'No data'
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon />
            {isIrrigation ? 'Output Readings' : 'Sensor Readings'} - {selectedDate.toLocaleDateString()}
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : reportData && reportData.data.readings.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                    {isIrrigation ? (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Enabled</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>CH1</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>CH2</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>CH3</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>CH4</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>pH Value</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{isTds ? 'TDS Value (ppm)' : 'EC Value (μS/cm)'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.data.readings.map((reading) => (
                    <TableRow 
                      key={reading.id} 
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f7ff' }
                      }}
                    >
                      <TableCell>
                        {formatTime(reading.timestamp)}
                      </TableCell>
                      {isIrrigation ? (
                        <>
                          <TableCell>
                            <Chip
                              label={reading.enabled ? 'Enabled' : 'Disabled'}
                              size="small"
                              color={reading.enabled ? 'success' : 'default'}
                              variant={reading.enabled ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          {['ch1','ch2','ch3','ch4'].map((ch) => (
                            <TableCell key={ch}>
                              <Chip
                                label={reading.outputs?.[ch] ? 'ON' : 'OFF'}
                                size="small"
                                color={reading.outputs?.[ch] ? 'success' : 'default'}
                                variant={reading.outputs?.[ch] ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                          ))}
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: getPhColor(reading.phValue)
                                }}
                              >
                                {reading.phValue}
                              </Typography>
                              {reading.phValue < 6.0 && (
                                <Chip label="Acidic" size="small" color="error" />
                              )}
                              {reading.phValue > 8.0 && (
                                <Chip label="Alkaline" size="small" color="warning" />
                              )}
                              {reading.phValue >= 6.0 && reading.phValue <= 8.0 && (
                                <Chip label="Optimal" size="small" color="success" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: getEcColor(reading.ecValue)
                                }}
                              >
                                {reading.ecValue}
                              </Typography>
                              {reading.ecValue < 800 && (
                                <Chip label="Low" size="small" color="info" />
                              )}
                              {reading.ecValue > 1800 && (
                                <Chip label="High" size="small" color="warning" />
                              )}
                              {reading.ecValue >= 800 && reading.ecValue <= 1800 && (
                                <Chip label="Normal" size="small" color="success" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {(reading.phValue >= 6.0 && reading.phValue <= 8.0 && 
                              reading.ecValue >= 800 && reading.ecValue <= 1800) ? (
                              <Chip label="Good" size="small" color="success" />
                            ) : (
                              <Chip label="Attention" size="small" color="warning" />
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={reportData.pagination.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No data available for the selected date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try selecting a different date or device
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Report;
